<template>
  <div class="scan-results">
    <div v-if="successMessage" class="success-toast">
      {{ successMessage }}
    </div>
    <div v-if="errorMessage" class="error-toast">
      {{ errorMessage }}
    </div>
    <div v-if="!loading" class="header">
      <div class="header-content">
        <div class="header-top">
          <div class="header-left">
            <button @click="$router.go(-1)" class="back-button">
              <span class="back-arrow">←</span>
              <span class="back-text">Back</span>
            </button>
            <div class="mobile-header-text">
              <h2 class="domain-title">{{ domainName }}</h2>
              <p class="mobile-page-subtitle">Scan Results</p>
            </div>
          </div>
          <h2 class="page-title">Scan Results</h2>
        </div>
        <div class="domain-info">
          <span class="domain-name">{{ domainName }}</span>
          <span class="changes-chip">
            <img src="../assets/changes.png" alt="Changes" class="changes-icon">
            {{ totalChanges }} changes
          </span>
          <span class="nodes-chip">
            <img src="../assets/changes.png" alt="Nodes" class="nodes-icon">
            {{ nodesScanned }} nodes scanned
          </span>
        </div>
        <div class="last-scanned">Last scanned: {{ lastScannedAt }}</div>
      </div>
    </div>

    <div v-if="loading" class="loading">
      Loading scan results...
    </div>
    <div v-else-if="error" class="error">
      {{ error }}
    </div>
    <div v-else-if="noScanResults" class="no-scan-results">
      <div class="no-scan-message">{{ translateErrorCode('SCAN_RESULT_NOT_FOUND') }}</div>
      <div class="manual-scan-container">
        <button @click="runManualScan" class="manual-scan-button" :disabled="scanningInProgress">
          <span v-if="scanningInProgress" class="button-spinner"></span>
          <span v-else>Run Manual Scan</span>
        </button>
      </div>
    </div>
    <!-- Filter Chips -->
    <div v-if="!loading && !error && !noScanResults && changes && changes.hasChanges" class="filter-chips">
      <div class="filter-chips-label">Filter by extension:</div>
      <div class="filter-chips-container">
        <button 
          @click="toggleFilter('.php')" 
          :class="['filter-chip', { active: activeFilters.includes('.php') }]"
        >
          .php
        </button>
        <button 
          @click="toggleFilter('.js')" 
          :class="['filter-chip', { active: activeFilters.includes('.js') }]"
        >
          .js
        </button>
        <button 
          v-if="activeFilters.length > 0"
          @click="activeFilters = []" 
          class="filter-chip clear-all"
        >
          Clear all
        </button>
      </div>
    </div>

    <!-- No Changes Detected -->
    <div v-if="!loading && !error && !noScanResults && changes && !changes.hasChanges" class="no-changes-detected">
      <div class="checkmark">
        <div class="checkmark-circle">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
      </div>
      <div class="no-changes-message">No changes detected</div>
      <div class="no-changes-description">Your domain scan completed successfully with no modifications found.</div>
      <div v-if="isEnabled" class="manual-scan-container">
        <button @click="runManualScan" class="manual-scan-button" :disabled="scanningInProgress">
          <span v-if="scanningInProgress" class="button-spinner"></span>
          <span v-else>Run Manual Scan</span>
        </button>
      </div>
    </div>

    <div v-if="!loading && !error && !noScanResults && changes && changes.hasChanges" class="changes-section">
      <!-- Tab Navigation -->
      <div class="tab-navigation">
        <button 
          @click="switchTab('added')" 
          :class="['tab-button', { active: activeTab === 'added' }]"
        >
          <span class="tab-title">
            Added ({{ filteredAdded.length }})
            <span v-if="showDotOnAdded" class="tab-dot"></span>
          </span>
        </button>
        <button 
          @click="switchTab('removed')" 
          :class="['tab-button', { active: activeTab === 'removed' }]"
        >
          <span class="tab-title">
            Removed ({{ filteredRemoved.length }})
            <span v-if="showDotOnRemoved" class="tab-dot"></span>
          </span>
        </button>
        <button 
          @click="switchTab('changed')" 
          :class="['tab-button', { active: activeTab === 'changed' }]"
        >
          <span class="tab-title">
            Changed ({{ filteredChanged.length }})
            <span v-if="showDotOnChanged" class="tab-dot"></span>
          </span>
        </button>
      </div>

      <!-- Tab Content -->
      <div class="tab-content">
        <!-- Added Files Tab -->
        <div v-if="activeTab === 'added'" class="tab-panel">
          <div v-if="filteredAdded.length > 0" class="file-list">
            <div v-for="(file, index) in filteredAdded" :key="file.path" class="file-item" @click.stop>
              <span class="file-path">{{ file.path }}</span>
              <span class="file-details">
                {{ formatBytes(file.size) }} | {{ file.modTime }}
              </span>
              <div class="file-actions">
                <button @click.stop="excludeFile(file.path)" class="exclude-button-desktop">exclude</button>
                <div class="file-menu-container">
                  <button @click.stop="toggleMenu(`added-${index}`)" class="menu-dots-button">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="8" cy="3" r="1.5" fill="currentColor"/>
                      <circle cx="8" cy="8" r="1.5" fill="currentColor"/>
                      <circle cx="8" cy="13" r="1.5" fill="currentColor"/>
                    </svg>
                  </button>
                  <div v-if="openMenuIndex === `added-${index}`" class="file-menu-dropdown">
                    <button @click.stop="excludeFile(file.path)" class="menu-item">Exclude</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="no-files">
            <span v-if="changes && changes.added && changes.added.length > 0">
              No files match the current filter
            </span>
            <span v-else>
              No files added
            </span>
          </div>
        </div>

        <!-- Removed Files Tab -->
        <div v-if="activeTab === 'removed'" class="tab-panel">
          <div v-if="filteredRemoved.length > 0" class="file-list">
            <div v-for="(file, index) in filteredRemoved" :key="file.path" class="file-item" @click.stop>
              <span class="file-path">{{ file.path }}</span>
              <span class="file-details">
                {{ formatBytes(file.size) }} | {{ file.modTime }}
              </span>
              <div class="file-actions">
                <button @click.stop="excludeFile(file.path)" class="exclude-button-desktop">exclude</button>
                <div class="file-menu-container">
                  <button @click.stop="toggleMenu(`removed-${index}`)" class="menu-dots-button">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="8" cy="3" r="1.5" fill="currentColor"/>
                      <circle cx="8" cy="8" r="1.5" fill="currentColor"/>
                      <circle cx="8" cy="13" r="1.5" fill="currentColor"/>
                    </svg>
                  </button>
                  <div v-if="openMenuIndex === `removed-${index}`" class="file-menu-dropdown">
                    <button @click.stop="excludeFile(file.path)" class="menu-item">Exclude</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="no-files">
            <span v-if="changes && changes.removed && changes.removed.length > 0">
              No files match the current filter
            </span>
            <span v-else>
              No files removed
            </span>
          </div>
        </div>

        <!-- Changed Files Tab -->
        <div v-if="activeTab === 'changed'" class="tab-panel">
          <div v-if="filteredChanged.length > 0" class="file-list">
            <div v-for="(file, index) in filteredChanged" :key="file.path" class="file-item" @click.stop>
              <span class="file-path">{{ file.path }}</span>
              <span class="file-details">
                {{ formatBytes(file.size) }} | {{ file.modTime }}
              </span>
              <div class="file-actions">
                <button @click.stop="excludeFile(file.path)" class="exclude-button-desktop">exclude</button>
                <div class="file-menu-container">
                  <button @click.stop="toggleMenu(`changed-${index}`)" class="menu-dots-button">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="8" cy="3" r="1.5" fill="currentColor"/>
                      <circle cx="8" cy="8" r="1.5" fill="currentColor"/>
                      <circle cx="8" cy="13" r="1.5" fill="currentColor"/>
                    </svg>
                  </button>
                  <div v-if="openMenuIndex === `changed-${index}`" class="file-menu-dropdown">
                    <button @click.stop="excludeFile(file.path)" class="menu-item">Exclude</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="no-files">
            <span v-if="changes && changes.changed && changes.changed.length > 0">
              No files match the current filter
            </span>
            <span v-else>
              No files changed
            </span>
          </div>
        </div>
      </div>

      <div v-if="isEnabled" class="manual-scan-container">
        <button @click="runManualScan" class="manual-scan-button" :disabled="scanningInProgress">
          <span v-if="scanningInProgress" class="button-spinner"></span>
          <span v-else>Run Manual Scan</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { api } from '../utils/api'

export default {
  name: 'ScanResultsView',
  inject: ['env'],
  data() {
    return {
      domainName: '',
      lastScannedAt: '',
      totalChanges: 0,
      nodesScanned: 0,
      changes: null,
      loading: true,
      error: null,
      noScanResults: false,
      successMessage: null,
      errorMessage: null,
      isEnabled: true,
      activeTab: 'added',
      activeFilters: [],
      viewedTabs: new Set(),
      openMenuIndex: null,
      scanningInProgress: false
    }
  },
  computed: {
    filteredAdded() {
      return this.filterFiles(this.changes?.added || []);
    },
    filteredChanged() {
      return this.filterFiles(this.changes?.changed || []);
    },
    filteredRemoved() {
      return this.filterFiles(this.changes?.removed || []);
    },
    availableTabs() {
      const tabs = [];
      if (this.filteredAdded.length > 0) tabs.push('added');
      if (this.filteredRemoved.length > 0) tabs.push('removed');
      if (this.filteredChanged.length > 0) tabs.push('changed');
      return tabs;
    },
    showDotOnAdded() {
      return this.filteredAdded.length > 0 && !this.viewedTabs.has('added');
    },
    showDotOnRemoved() {
      return this.filteredRemoved.length > 0 && !this.viewedTabs.has('removed');
    },
    showDotOnChanged() {
      return this.filteredChanged.length > 0 && !this.viewedTabs.has('changed');
    }
  },
  async created() {
    try {
      await Promise.all([
        this.fetchDomainDetails(),
        this.fetchScanResults()
      ])
    } finally {
      this.loading = false
    }
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside)
  },
  watch: {
    availableTabs: {
      handler(newTabs) {
        // If current tab is not in available tabs, switch to first available tab
        if (newTabs.length > 0 && !newTabs.includes(this.activeTab)) {
          this.activeTab = newTabs[0];
        }
      },
      immediate: true
    }
  },
  methods: {
    translateErrorCode(code) {
      const translations = {
        'SCAN_RESULT_NOT_FOUND': 'No scan result found, run a manual scan for results'
      }
      return translations[code] || 'An error occurred'
    },

    async fetchDomainDetails() {
      try {
        const domainData = await api(`/domains/${this.$route.params.id}`, {}, this.$router)
        this.domainName = domainData.name || 'Unknown Domain'

        this.lastScannedAt = domainData.lastScanAt ? this.formatDate(domainData.lastScanAt) : 'Never scanned'

        this.isEnabled = domainData.isEnabled !== false
      } catch (error) {
        console.error('Error fetching domain details:', error)
        this.domainName = 'Unknown Domain'
        this.lastScannedAt = 'Unknown'
        this.isEnabled = true
      }
    },

    async fetchScanResults() {
      try {
        const scanId = this.$route.params.scanId
        const apiUrl = scanId
          ? `/domains/${this.$route.params.id}/scan-result?scan=${scanId}`
          : `/domains/${this.$route.params.id}/scan-result`

        const data = await api(apiUrl, {}, this.$router)

        if (data) {
          this.changes = this.formatChanges(data.results)
          this.calculateTotalChanges(data.results)
          this.nodesScanned = data.nodesScanned || 0
          // Mark the initial active tab as viewed
          if (this.activeTab && this.availableTabs.includes(this.activeTab)) {
            this.viewedTabs.add(this.activeTab)
          }
        } else {
          this.error = 'No scan results found for this domain'
        }
      } catch (error) {
        if (error.code === 'SCAN_RESULT_NOT_FOUND') {
          this.noScanResults = true
          this.error = null
        } else {
          this.error = error.code || 'Something went wrong please contact support'
          this.noScanResults = false
        }
      }
    },

    formatChanges(results) {
      if (!results) return null;

      const formatDate = (timestamp) => {
        const date = new Date(timestamp * 1000);
        return date.toLocaleString('el-GR', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        });
      };

      const changes = {
        added: results.added ? Object.entries(results.added).map(([path, details]) => ({
          path,
          type: details.type,
          size: details.size,
          modTime: formatDate(details.modTime)
        })) : [],
        removed: results.removed ? Object.entries(results.removed).map(([path, details]) => ({
          path,
          type: details.type,
          size: details.size,
          modTime: formatDate(details.modTime)
        })) : [],
        changed: results.changed ? Object.entries(results.changed).map(([path, details]) => ({
          path,
          type: details.type,
          size: details.size,
          modTime: formatDate(details.modTime)
        })) : []
      };

      return {
        ...changes,
        hasChanges: changes.added.length > 0 || changes.removed.length > 0 || changes.changed.length > 0
      };
    },

    formatBytes(bytes) {
      const units = ['B', 'KB', 'MB', 'GB', 'TB'];
      let size = bytes;
      let unitIndex = 0;

      while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024;
        unitIndex++;
      }

      return `${size.toFixed(2)} ${units[unitIndex]}`;
    },

    formatDate(dateString) {
      if (!dateString) return 'Never scanned';
      
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
    },

    calculateTotalChanges(results) {
      if (!results) {
        this.totalChanges = 0;
        return;
      }
      
      const addedCount = Object.keys(results.added || {}).length;
      const removedCount = Object.keys(results.removed || {}).length;
      const changedCount = Object.keys(results.changed || {}).length;
      
      this.totalChanges = addedCount + removedCount + changedCount;
    },

    toggleMenu(menuIndex) {
      if (this.openMenuIndex === menuIndex) {
        this.openMenuIndex = null;
      } else {
        this.openMenuIndex = menuIndex;
      }
    },

    handleClickOutside(event) {
      if (this.openMenuIndex && !event.target.closest('.file-menu-container')) {
        this.openMenuIndex = null;
      }
    },

    async excludeFile(filename) {
      try {
        // Close menu before making the API call
        this.openMenuIndex = null;
        
        await api(`/domains/${this.$route.params.id}/exclude`, {
          method: 'POST',
          body: JSON.stringify({
            name: filename,
            add: true
          })
        }, this.$router)

        this.successMessage = `File "${filename}" excluded successfully!`;
        setTimeout(() => {
          this.successMessage = null;
        }, 3000);

        // Don't refetch results immediately to avoid potential reload issues
        // The user can manually refresh if needed
      } catch (error) {
        // Use code from error response if available, otherwise use default message
        this.errorMessage = error.code || 'Something went wrong please contact support';
        setTimeout(() => {
          this.errorMessage = null;
        }, 3000);
      }
    },

    switchTab(tab) {
      this.activeTab = tab;
      this.viewedTabs.add(tab);
    },

    toggleFilter(extension) {
      const index = this.activeFilters.indexOf(extension);
      if (index > -1) {
        this.activeFilters.splice(index, 1);
      } else {
        this.activeFilters.push(extension);
      }
    },

    filterFiles(files) {
      if (this.activeFilters.length === 0) {
        return files;
      }
      return files.filter(file => 
        this.activeFilters.some(filter => file.path.endsWith(filter))
      );
    },

    async runManualScan() {
      this.scanningInProgress = true;
      try {
        const domainId = this.$route.params.id;
        const response = await api(`/domains/${domainId}/scan`, {
          method: 'POST'
        }, this.$router);
        
        // If we reach here without an exception, the API call was successful (200 status)
        // The response can be null (empty body), an object with status, or an object with error
        if (response === null) {
          // Empty response body with 200 status - this is success
          this.successMessage = 'Manual scan started successfully!';
          
          // Go back to previous page (admin list or domains list)
          setTimeout(() => {
            this.$router.go(-1);
          }, 1500);
        } else if (response && response.error && response.code) {
          // Response contains an error
          this.errorMessage = `Error: ${response.code}`;
          this.scanningInProgress = false;
          setTimeout(() => {
            this.errorMessage = null;
          }, 3000);
        } else if (response && (response.status === 'success' || response.status === 200)) {
          // Response has explicit success status
          this.successMessage = 'Manual scan started successfully!';
          
          // Go back to previous page (admin list or domains list)
          setTimeout(() => {
            this.$router.go(-1);
          }, 1500);
        } else {
          // Any other response is considered success (since we got 200 status)
          this.successMessage = 'Manual scan started successfully!';
          
          // Go back to previous page (admin list or domains list)
          setTimeout(() => {
            this.$router.go(-1);
          }, 1500);
        }
      } catch (error) {
        // Use code from error response if available, otherwise use default message
        this.errorMessage = error.code || 'Something went wrong please contact support';
        this.scanningInProgress = false;
        
        setTimeout(() => {
          this.errorMessage = null;
        }, 3000);
      }
    }
  }
}
</script>

<style scoped>
.scan-results {
  max-width: 1460px;
  margin: 0 auto;
  padding: 20px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 30px;
}

.header-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
}

.header-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 24px;
}

.page-title {
  margin: 0;
}

.domain-title {
  display: none;
  margin: 0;
}

.mobile-header-text {
  display: none;
  flex-direction: column;
  gap: 2px;
}

.mobile-page-subtitle {
  margin: 0;
  color: var(--text-muted);
  font-size: 12px;
  font-weight: normal;
}

.domain-info {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 40px;
  flex-wrap: wrap;
}

.domain-name {
  color: white;
  font-size: 24px;
  font-weight: 500;
}

.changes-chip {
  background-color: #374151;
  color: #9CA3AF;
  padding: 6px 10px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 4px;
}

.changes-icon {
  font-size: 10px;
}

.nodes-chip {
  background-color: #374151;
  color: #9CA3AF;
  padding: 6px 10px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 4px;
}

.nodes-icon {
  font-size: 10px;
}

.last-scanned {
  color: var(--text-muted);
  font-size: 14px;
  font-weight: normal;
  margin-top: 2px;
}

.back-button {
  padding: 6px 12px;
  background-color: var(--bg-tertiary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.75rem;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;
}

.back-button:hover {
  background-color: var(--border-color);
}

.back-arrow {
  font-size: 1.2em;
  font-weight: bold;
}

.back-text {
  display: inline;
}

.loading, .error {
  text-align: center;
  padding: 40px;
  font-size: 1.2em;
  color: var(--text-primary);
}

.error {
  color: var(--error-color);
}

.no-scan-results {
  text-align: center;
  padding: 40px;
  background: var(--bg-secondary);
  border-radius: 8px;
  box-shadow: var(--shadow);
  border: 1px solid var(--border-color);
}

.no-scan-message {
  font-size: 1.2em;
  color: var(--text-primary);
  margin-bottom: 20px;
}

.no-changes-detected {
  text-align: center;
  padding: 60px 40px;
  background: var(--bg-secondary);
  border-radius: 8px;
  box-shadow: var(--shadow);
  border: 1px solid var(--border-color);
  margin-bottom: 20px;
}

.checkmark {
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
}

.checkmark-circle {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  border: 4px solid var(--success-color, #10b981);
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: var(--success-color, #10b981);
}

.no-changes-message {
  font-size: 1.5em;
  color: var(--text-primary);
  margin-bottom: 12px;
  font-weight: 600;
}

.no-changes-description {
  color: var(--text-muted);
  font-size: 1em;
  margin-bottom: 30px;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
}

.changes-section {
  background: var(--bg-secondary);
  border-radius: 8px;
  box-shadow: var(--shadow);
  border: 1px solid var(--border-color);
  padding: 0;
  overflow: hidden;
}

.tab-navigation {
  display: flex;
  background: var(--bg-tertiary);
  border-bottom: 1px solid var(--border-color);
}

.tab-button {
  flex: 1;
  padding: 16px 20px;
  background: transparent;
  border: none;
  color: var(--text-muted);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border-bottom: 3px solid transparent;
  position: relative;
}

.tab-title {
  position: relative;
  display: inline-block;
}

.tab-dot {
  position: absolute;
  top: -4px;
  right: -8px;
  width: 8px;
  height: 8px;
  background-color: #ef4444;
  border-radius: 50%;
  border: 1px solid var(--bg-secondary);
}

.tab-button:hover {
  background: var(--bg-secondary);
  color: var(--text-primary);
}

.tab-button.active {
  color: var(--text-primary);
  background: var(--bg-secondary);
  border-bottom-color: var(--border-color);
}

.tab-content {
  padding: 20px;
}

.tab-panel {
  min-height: 200px;
}

.filter-chips {
  padding: 16px 20px;
  background: var(--bg-secondary);
  border-radius: 8px;
  box-shadow: var(--shadow);
  border: 1px solid var(--border-color);
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.filter-chips-label {
  color: var(--text-muted);
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
}

.filter-chips-container {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.filter-chip {
  padding: 6px 12px;
  background: var(--bg-secondary);
  color: var(--text-muted);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.filter-chip:hover {
  background: var(--border-color);
  color: var(--text-primary);
}

.filter-chip.active {
  background: var(--accent-color);
  color: white;
  border-color: var(--accent-color);
}

.filter-chip.clear-all {
  background: var(--error-color);
  color: white;
  border-color: var(--error-color);
}

.filter-chip.clear-all:hover {
  background: #dc2626;
  border-color: #dc2626;
}

.file-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.file-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2px 10px;
  background: var(--bg-tertiary);
  border-radius: 4px;
  border: 1px solid var(--border-color);
  transition: background-color 0.15s ease-in-out, transform 0.1s ease-in-out;
}

.file-item:hover {
  background-color: var(--border-color);
  transform: translateY(-1px);
  box-shadow: var(--shadow-light);
}

.file-path {
  font-family: 'IBM Plex Mono', monospace;
  color: var(--text-primary);
  flex: 1;
  font-size: 12px;
}

.file-details {
  color: var(--text-muted);
  font-size: 12px;
  margin: 0 12px;
  white-space: nowrap;
}

.file-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.exclude-button-desktop {
  color: var(--text-muted);
  text-decoration: none;
  font-size: 12px;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 3px;
  transition: all 0.2s;
  background-color: var(--bg-secondary);
  border: 1px solid var(--border-color);
  display: none;
}

.exclude-button-desktop:hover {
  color: var(--text-primary);
  background-color: var(--border-color);
  border-color: var(--accent-color);
}

.file-menu-container {
  position: relative;
  display: flex;
  align-items: center;
}

.menu-dots-button {
  background: transparent;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;
  min-width: 24px;
  min-height: 24px;
}

.menu-dots-button:hover {
  background-color: var(--bg-secondary);
  color: var(--text-primary);
}

.file-menu-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 4px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 4px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  z-index: 100;
  min-width: 120px;
  overflow: hidden;
}

.menu-item {
  width: 100%;
  padding: 8px 12px;
  background: transparent;
  border: none;
  color: var(--text-primary);
  text-align: left;
  cursor: pointer;
  font-size: 12px;
  transition: background-color 0.2s;
}

.menu-item:hover {
  background-color: var(--bg-tertiary);
}

.exclude-button {
  display: none;
}

.no-changes {
  text-align: center;
  padding: 40px;
  color: var(--text-muted);
  font-size: 1.1em;
}

.no-files {
  text-align: center;
  padding: 20px;
  color: var(--text-muted);
  font-size: 12px;
  font-style: italic;
}

.success-toast {
  background-color: var(--success-color);
  color: white;
  padding: 12px 24px;
  border-radius: 4px;
  margin-bottom: 20px;
  text-align: left;
  animation: slideIn 0.3s ease-out;
}

.error-toast {
  background-color: var(--error-color);
  color: white;
  padding: 12px 24px;
  border-radius: 4px;
  margin-bottom: 20px;
  text-align: left;
  animation: slideIn 0.3s ease-out;
}

.manual-scan-container {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid var(--border-color);
}



.manual-scan-button {
  padding: 10px 20px;
  background-color: var(--accent-color);
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  transition: background-color 0.2s;
  min-width: 150px;
  min-height: 42px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.manual-scan-button:hover:not(:disabled) {
  background-color: var(--accent-hover);
}

.manual-scan-button:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

.button-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes slideIn {
  from {
    transform: translateY(-100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

/* Desktop only styles */
@media (min-width: 769px) {
  .domain-info {
    margin-top: 30px;
  }

  .exclude-button-desktop {
    display: inline-block;
  }

  .file-menu-container {
    display: none;
  }
}

/* Responsive design for tabs */
@media (max-width: 768px) {
  .tab-navigation {
    flex-direction: column;
  }
  
  .tab-button {
    text-align: left;
    border-bottom: 1px solid var(--border-color);
    border-right: none;
  }
  
  .tab-button:last-child {
    border-bottom: none;
  }
  
  .tab-button.active {
    border-bottom-color: var(--border-color);
    border-right: 3px solid var(--border-color);
  }
}

@media (max-width: 768px) {
  .scan-results {
    padding: 12px;
  }

  .page-title {
    display: none;
  }

  .mobile-header-text {
    display: flex;
  }

  .domain-title {
    display: block;
    font-size: 1.8em;
    font-weight: 600;
    margin: 0;
  }

  .back-text {
    display: none;
  }

  .back-button {
    padding: 8px;
    min-width: 40px;
    justify-content: center;
  }

  .domain-info {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 20px;
  }

  .domain-name {
    display: none;
  }

  .header-top {
    justify-content: flex-start;
    align-items: flex-start;
    margin-bottom: 16px;
  }

  .header-left {
    align-items: flex-start;
  }

  .file-item {
    flex-direction: row;
    align-items: flex-start;
    padding: 8px;
    gap: 6px;
    flex-wrap: wrap;
  }

  .file-path {
    flex: 1 1 calc(100% - 32px);
    min-width: 0;
    word-break: break-all;
    font-size: 12px;
    line-height: 1.3;
    order: 1;
  }

  .exclude-button-desktop {
    display: none;
  }

  .file-actions {
    flex-shrink: 0;
    order: 2;
  }

  .file-menu-container {
    flex-shrink: 0;
    margin: 0;
    display: flex;
  }

  .file-details {
    margin: 0;
    font-size: 10px;
    width: 100%;
    order: 3;
    flex-basis: 100%;
    margin-top: 2px;
  }

  .menu-dots-button {
    min-width: 24px;
    min-height: 24px;
  }

  .file-menu-dropdown {
    right: 0;
    min-width: 100px;
  }

  .changes-section {
    margin-top: 12px;
  }

  .filter-chips {
    margin-bottom: 12px;
    padding: 10px 12px;
  }

  .tab-content {
    padding: 10px;
  }

  .file-list {
    gap: 4px;
  }

  .tab-dot {
    top: -2px;
    right: -6px;
    width: 6px;
    height: 6px;
  }
}

@media (max-width: 480px) {
  .scan-results {
    padding: 8px;
  }

  .tab-button {
    padding: 12px 16px;
    font-size: 13px;
  }

  .tab-content {
    padding: 12px;
  }

  .filter-chips {
    padding: 12px;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .filter-chips-container {
    width: 100%;
    justify-content: flex-start;
  }

  .filter-chip {
    padding: 8px 12px;
    font-size: 11px;
  }

  .domain-title {
    font-size: 1.5em;
  }

  .file-item {
    padding: 6px 8px;
    gap: 4px;
  }

  .file-path {
    font-size: 11px;
    line-height: 1.2;
  }

  .file-details {
    font-size: 9px;
    margin-top: 1px;
  }

  .file-menu-container {
    margin: 0;
  }

  .menu-dots-button {
    min-width: 20px;
    min-height: 20px;
    padding: 2px;
  }

  .file-menu-dropdown {
    min-width: 90px;
  }

  .menu-item {
    padding: 6px 10px;
    font-size: 11px;
  }

  .tab-content {
    padding: 8px;
  }

  .filter-chips {
    padding: 8px 10px;
    margin-bottom: 10px;
  }

  .file-list {
    gap: 3px;
  }

  .header {
    margin-bottom: 20px;
  }

  .changes-chip,
  .nodes-chip {
    font-size: 11px;
    padding: 5px 8px;
  }
}
</style> 